import { Icon, Input, Picker, t, Title } from "owc-react-lib-common";
import * as React from "react";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/Button";
import { Checkbox } from "src/components/Checkbox";
import { IconButton } from "src/components/IconButton";
import { Modal } from "src/components/Modal";
import { ModalConfirm } from "src/components/ModalConfirm";
import { ITableHeader, Table } from "src/components/Table";
import { Text } from "src/components/Text";
import { View } from "src/components/View";
import { ORGANIZER_READ_ONLY, OWCOpenDmrContext } from "src/context/OWCOpenDmrContext";
import { useAlerts } from "src/hooks/useAlerts";
import { useUserPermission } from "src/hooks/useUserPermission";
import { REQUIRED, useValidation } from "src/hooks/useValidation";
import { EventContext } from "src/pages/organizer/events/EventContext";
import { ApplySchemaDTO } from "src/services/GenerateCompetitionBySchemeService";
import * as GroupService from "src/services/GroupService";
import { ITeamLevelDTO } from "src/services/TeamLevelService";
import { StepperApplySchema } from "../../schema/StepperApplySchema";

interface IGroups {
  data: any[];
  eventCategoryId: number;
  teams: any[];
  onCreate: (object: any) => void;
  onDelete: (uuid: string) => void;
}

export const Groups = ({ data, eventCategoryId, onCreate, onDelete }: IGroups) => {
  const context = React.useContext(OWCOpenDmrContext);
  const form = React.useRef<any>();
  const { validateAll } = useValidation();
  const [errors, setErrors] = React.useState<any>({});
  const [groups, setGroups] = React.useState<any[]>(data);
  const [createGroupModal, setCreateGroupModal] = React.useState<boolean>(false);
  const [groupToChangeLevel, setGroupToChangeLevel] = React.useState<Partial<GroupService.IGroupDTO>>();
  const [groupToDelete, setGroupToDelete] = React.useState();
  const readOnly = useUserPermission(ORGANIZER_READ_ONLY);
  const navigate = useNavigate();
  const alerts = useAlerts();
  const [createGroupBySchemaModal, setCreateGroupBySchemaModal] = React.useState<Partial<ApplySchemaDTO>>();
  const { data: event } = useContext(EventContext);
  const [showEmptyLevels, setShowEmptyLevels] = React.useState(false);

  /**
   * validate
   */
  const validate = React.useCallback(() => {
    const validated = validateAll(form.current, [
      { name: "name", type: REQUIRED },
    ]);

    setErrors(validated.errors);
    return !validated.hasErrors;
  }, [validateAll]);

  /**
   * delete group
   */
  const deleteGroup = React.useCallback(async (object: GroupService.IGroupDTO) => {
    try {
      await GroupService.deleteGroup(object.uuid);
      onDelete(object.uuid);

      const filteredGroups = groups.filter(g => g.uuid !== object.uuid);
      filteredGroups.forEach((g, i) => g.groupOrder = i + 1);
      setGroups(filteredGroups);

      setGroupToDelete(undefined);
      alerts.create({ text: t("admin.events.group.deleteConfirmation"), type: "success" });
    } catch (error) {
      alerts.createDSMError(error);
    }
  }, [onDelete, alerts]);

  /**
   * open create group modal
   */
  const openCreateGroupModal = React.useCallback((group: Partial<GroupService.IGroupDTO> = {}) => {
    form.current = { eventCategoryId: eventCategoryId, standingVisible: true, ...group };
    setCreateGroupModal(true);
  }, []);

  /**
   * create group
   */
  const createGroup = React.useCallback(async () => {
    if (!validate()) return;

    try {
      const newGroup = await GroupService.createGroup(form.current);
      onCreate(newGroup.data);
      setGroups([...groups, newGroup.data])

      setCreateGroupModal(false);
      alerts.create({ text: t("admin.events.group.createConfirmation"), type: "success" });
    } catch (error) {
      alerts.createDSMError(error);
    }
  }, [onCreate, alerts, validate]);

  /**
   * change level
   */
  const changeLevel = React.useCallback(async () => {
    try {
      await GroupService.updateGroup(groupToChangeLevel);
      setGroups(prev => prev.map((group) => group.uuid === groupToChangeLevel.uuid ? groupToChangeLevel : group));
      setGroupToChangeLevel(undefined);
    } catch (error) {
      alerts.createDSMError(error);
    }
  }, [groupToChangeLevel]);

  /**
   * swap group order
   */
  const swapOrderGroups = async (groups: GroupService.IGroupDTO[], uuid1: string, uuid2: string) => {
    try {
      if (!uuid1 || !uuid2) {
        return;
      }

      await GroupService.swapOrder(uuid1, uuid2);

      const group1Order = groups?.find((group) => group.uuid === uuid1)?.groupOrder;
      const group2Order = groups?.find((group) => group.uuid === uuid2)?.groupOrder;

      setGroups(prev => prev.map((group) => {
        if (group.uuid === uuid1) {
          return { ...group, groupOrder: group2Order }
        }

        if (group.uuid === uuid2) {
          return { ...group, groupOrder: group1Order }
        }

        return group;
      }));
    } catch (error) {
      alerts.createDSMError(error);
    }
  }

  /**
   * update groups
   */
  const updateGroups = React.useCallback(async () => {
    try {
      const response = await GroupService.getByCategoryId(eventCategoryId);
      setGroups(response.data);
    } catch (error) {
      alerts.createDSMError(error);
    }
  },
    []
  );

  /**
   * on close schema application
   */
  const onCloseStepperApplySchema = async () => {
    await updateGroups();
    setCreateGroupBySchemaModal(undefined)
  }

  /**
   * render
   */
  const headers: ITableHeader[] = [
    {
      name: "groupOrder",
      label: t("admin.events.group.order"),
      headerTextProps: { textAlign: "center" },
      render: (object, rowIdx, objects) => {
        return (
          <View flexDirection="row" style={{ gap: 2, alignItems: 'center', justifyContent: "center" }}>
            <IconButton fontSize={14} backgroundColor="transparent" color={context.configuration.colors.primary.main} name="arrow-down-2" onPress={() => swapOrderGroups(objects, object.uuid, objects[rowIdx + 1]?.uuid)} />

            <View style={{ backgroundColor: context.configuration.colors.primary.main, borderRadius: 100, width: 25, height: 25, alignItems: "center", justifyContent: "center" }}>
              <Text color="white">{rowIdx + 1}</Text>
            </View>

            <IconButton fontSize={14} backgroundColor="transparent" color={context.configuration.colors.primary.main} name="arrow-up-2" onPress={() => swapOrderGroups(objects, object.uuid, objects[rowIdx - 1]?.uuid)} />
          </View>
        )
      },
      width: 100
    },
    {
      name: "name",
      label: t("admin.events.group.name"),
      textProps: { uppercase: true },
    },
    {
      name: "actions", label: '', width: 100, hidden: readOnly, render: (object) => (
        <View flexDirection="row" alignItems="center" justifyContent="flex-end">
          <IconButton
            backgroundColor="transparent" color="black"
            name="rotateLeft"
            title={t("admin.events.group.changeTeamLevel")}
            onPress={() => setGroupToChangeLevel(object)}
          />

          <IconButton
            backgroundColor="transparent" color="black"
            name="edit"
            title={t("global.edit")}
            onPress={() => navigate(`groups/${object.uuid}`)}
          />

          <IconButton
            backgroundColor="transparent" color="black"
            name="trash"
            title={t("global.delete")}
            onPress={() => {
              setGroupToDelete(object.uuid);
              setGroupToDelete({ ...object });
            }}
          />
        </View>
      )
    },
  ];

  const levels = event.eventCategories.find((eventCategory) => eventCategory.id === eventCategoryId)?.teamLevels.reduce((acc, cur: ITeamLevelDTO) => {
    acc.push({
      id: cur.id,
      label: cur.name,
      groups: groups.filter((group) => group.teamLevelId === cur.id),
    });

    return acc;
  }, []);

  levels.splice(0, 0, {
    label: t("global.undefined"),
    groups: groups.filter((group) => !group.teamLevelId),
  });

  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View style={{ flexDirection: "row", gap: 5, flex: 1 }}>
          <Button
            label={t("admin.events.group.create")}
            onPress={openCreateGroupModal}
            disabled={readOnly}
          />

          <Button
            label={t("admin.events.group.createbyschema")}
            onPress={() => setCreateGroupBySchemaModal({})}
            disabled={readOnly}
          />
        </View>

        <Checkbox
          label={t("admin.events.group.showEmptyLevels")}
          defaultValue={showEmptyLevels}
          onChange={setShowEmptyLevels} required={null}
          showRequiredIndicator={false}
        />
      </View>

      {levels.map((level, idx) => {
        if (!showEmptyLevels && !level.groups.length) {
          return null;
        }

        return (
          <View key={level.id ?? "undefined"}>
            <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 10 }}>
              <Title text={level.label} size="small" color={context.configuration.colors.primary.main as string} uppercase containerStyle={{ flex: 1, marginBottom: 0, paddingBottom: 0 }} />
              <Icon type="plus" title={t("admin.events.group.create")} color="white" backgroundColor={context.configuration.colors.primary.main} containerStyle={{ borderRadius: 5 }} onPress={() => openCreateGroupModal({ teamLevelId: level.id })} />
              <Icon type="download" title={t("admin.events.applyCompetitionSchema")} color="white" backgroundColor={context.configuration.colors.primary.main} containerStyle={{ borderRadius: 5 }} onPress={() => setCreateGroupBySchemaModal({ teamLevelId: level.id })} />
            </View>

            {!!level.groups.length ? (
              <Table
                defaultSort="groupOrder"
                defaultSize={25}
                headers={headers}
                data={level.groups}
                pagination={false}
                sortable={false}
              />
            ) : (
              <View style={{ marginBottom: idx < levels.length - 1 && 50, paddingVertical: 10, borderTopWidth: 1, borderBottomWidth: 1, borderColor: "#ccc" }}>
                <Text uppercase>{t("global.empty")}</Text>
              </View>
            )}
          </View>
        );
      })}

      <Modal visible={!!groupToChangeLevel} title={t("admin.events.group.changeTeamLevel")} onClose={() => setGroupToChangeLevel(undefined)}>
        <Picker items={levels.map((level) => ({ label: level.label, value: level.id }))} defaultValue={groupToChangeLevel?.teamLevelId} defaultOptionLabel={null} onChange={(value) => setGroupToChangeLevel({ ...groupToChangeLevel, teamLevelId: parseInt(value) })} />
        <Button label={t("global.save")} onPress={changeLevel} marginBottom={0} />
      </Modal>

      <Modal visible={createGroupModal} title={t("admin.events.group.create")} onClose={() => setCreateGroupModal(false)} width={550}>
        <Input label={t("admin.events.group.name")} onChange={(val) => (form.current.name = val)} error={errors.name} required />
        <Picker items={levels.map((level) => ({ label: level.label, value: level.id }))} defaultValue={form.current?.teamLevelId} defaultOptionLabel={null} onChange={(value) => form.current.teamLevelId = parseInt(value)} />
        <Button label={t("global.create")} onPress={createGroup} marginBottom={0} />
      </Modal>

      <ModalConfirm
        visible={!!groupToDelete}
        text={t("admin.events.group.deleteQuestion")}
        onSuccess={() => deleteGroup(groupToDelete)}
        onClose={() => setGroupToDelete(undefined)}
      />

      {createGroupBySchemaModal && (
        <StepperApplySchema
          eventCategoryId={eventCategoryId}
          teamLevelId={createGroupBySchemaModal?.teamLevelId}
          levels={event.eventCategories.find((eventCategory) => eventCategory.id === eventCategoryId)?.teamLevels}
          onClose={() => onCloseStepperApplySchema()}
        />
      )}
    </View>
  );
};